import { Blockquote } from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import { Color } from "@tiptap/extension-color";
import Document from "@tiptap/extension-document";
import Dropcursor from "@tiptap/extension-dropcursor";
import { FontFamily } from "@tiptap/extension-font-family";
import Gapcursor from "@tiptap/extension-gapcursor";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import HistoryExt from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import { Table, TableCell, TableHeader, TableKit, TableRow } from "@tiptap/extension-table";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { type Editor, type Extension, type Extensions, type Mark, type Node } from "@tiptap/react";

import EmojiExtension from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EmojiExtension";
import EntityLinkExtension from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkExtension";
import FontSizeExtension from "commonComponents/btWrappers/editor/core/extensions/custom/FontSizeExtension";
import IndentParagraphExtension from "commonComponents/btWrappers/editor/core/extensions/custom/IndentParagraphExtension";
import { MentionUserExtension } from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";
import OverrideTopLevelEnterKeyExtension from "commonComponents/btWrappers/editor/core/extensions/custom/OverrideTopLevelEnterKeyExtension";
import { TemplateVariableExtension } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";
import {
    type IBasicFeatureSetConfig,
    type IChatFeatureSetConfig,
    type IChatWithImageFeatureSetConfig,
    type IStandardFeatureSetConfig,
} from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";
import { FeatureSetBuilder } from "commonComponents/btWrappers/editor/core/extensions/featureSetBuilder";

import ImageExtension from "./custom/image/ImageExtension";
import TitleExtension from "./custom/TitleExtension";

const ExtensionNames = {
    document: Document.name,
    text: Text.name,
    heading: Heading.name,
    bold: Bold.name,
    italic: Italic.name,
    underline: Underline.name,
    strike: Strike.name,
    hardBreak: HardBreak.name,
    history: HistoryExt.name,
    gapcursor: Gapcursor.name,
    bulletList: BulletList.name,
    orderedList: OrderedList.name,
    listItem: ListItem.name,
    link: Link.name,
    placeholder: Placeholder.name,
    dropcursor: Dropcursor.name,
    textAlign: TextAlign.name,
    paragraph: Paragraph.name,
    textStyle: TextStyle.name,
    highlight: Highlight.name,
    blockquote: Blockquote.name,
    fontFamily: FontFamily.name,
    color: Color.name,
    tableKit: TableKit.name,
    table: Table.name,
    tableRow: TableRow.name,
    tableHeader: TableHeader.name,
    tableCell: TableCell.name,

    // Core/implicit extensions go here
    clipboardTextSerializer: "clipboardTextSerializer",
    editable: "editable",
    focusEvents: "focusEvents",
    keymap: "keymap",
    tabIndex: "tabindex",
    commands: "commands",
    paste: "paste",
    drop: "drop",
    delete: "delete",
    textDirection: "textDirection",

    // Custom extensions go here
    image: ImageExtension.name,
    title: TitleExtension.name,
    indentParagraph: IndentParagraphExtension.name,
    fontSize: FontSizeExtension.name,
    emoji: EmojiExtension.name,
    overrideTopLevelEnterKey: OverrideTopLevelEnterKeyExtension.name,
    entityLink: EntityLinkExtension.name,
    mentionUser: MentionUserExtension.name,
    templateVariable: TemplateVariableExtension.name,
};
export type ExtensionName = keyof typeof ExtensionNames;

const extensionNameReverseMap = Object.entries(ExtensionNames).reduce(
    (revMap, [key, value]) => {
        revMap[value] = key as ExtensionName;
        return revMap;
    },
    {} as Record<string, ExtensionName>,
);

/**
 * Accepts a list of extensions and returns a set of all of the extension names in the list
 * @param extensions array of TipTap extensions
 * @returns Set of all extension names in the passed list
 */
export const getExtensionNames = (extensions: Extensions) => {
    return extensions.reduce((setOfNames, extension) => {
        if (!(extension.name in extensionNameReverseMap)) {
            throw new Error(
                `Editor extension [${extension.name}] not known. Are you using an extension without registering it?`,
            );
        }
        setOfNames.add(extensionNameReverseMap[extension.name]);
        return setOfNames;
    }, new Set<ExtensionName>());
};

export function findExtension<E extends Extension | Node | Mark>(editor: Editor, extension: E) {
    return editor.extensionManager.extensions.find((ext): ext is E => ext.name === extension.name);
}

const getBasicFeatureBuilder = (extensionConfig: IBasicFeatureSetConfig) => {
    return FeatureSetBuilder.create(extensionConfig)
        .withHeading(extensionConfig)
        .withBold(extensionConfig)
        .withItalic()
        .withUnderline()
        .withStrike()
        .withHardbreak()
        .withLists(extensionConfig);
};

const getChatFeatureBuilder = (extensionConfig: IChatFeatureSetConfig): FeatureSetBuilder => {
    return FeatureSetBuilder.create(extensionConfig)
        .withHeading(extensionConfig)
        .withBold(extensionConfig)
        .withItalic()
        .withUnderline()
        .withStrike()
        .withHighlight()
        .withBlockquote(extensionConfig)
        .withHardbreak()
        .withOverrideTopLevelEnterKey(extensionConfig)
        .withEmoji()
        .withLists(extensionConfig)
        .withLink(extensionConfig)
        .withEntityLink(extensionConfig)
        .withMentionUser(extensionConfig);
};

/**
 * Gets a list of all of the extensions for the basic editor.
 *
 * Note: `getBasicFeatureset` is a subset of `getStandardFeatureset`
 * @param extensionConfig Configurations and event handler bindings for the various extensions
 * @returns An array containing all of the extensions supported for the "basic" editor
 */
export const getBasicFeatureset = (extensionConfig: IBasicFeatureSetConfig): Extensions => {
    return getBasicFeatureBuilder(extensionConfig).build();
};

/**
 * Gets a list of all of the extensions for the standard editor.
 *
 * Note: `getStandardFeatureset` is a superset of `getBasicFeatureset`
 * @param extensionConfig Configurations and event handler bindings for the various extensions
 * @returns An array containing all of the extensions supported for the "standard" editor
 */
export const getStandardFeatureset = (extensionConfig: IStandardFeatureSetConfig): Extensions => {
    return getBasicFeatureBuilder(extensionConfig)
        .withTitle()
        .withLink(extensionConfig)
        .withEntityLink(extensionConfig)
        .withImage(extensionConfig)
        .withTextAlign()
        .withIndentableParagraphs()
        .withFontSize()
        .withFontFamily()
        .withTextColor()
        .withHighlight()
        .withTable()
        .withBlockquote(extensionConfig)
        .withTemplateVariable(extensionConfig)
        .withEmoji()
        .build();
};

/**
 * Gets a list of all of the extensions for the chat editor.
 *
 * Note: `getChatFeatureset` is a subset of `getChatWithImagesFeatureset`
 * @param extensionConfig Configurations and event handler bindings for the various extensions
 * @returns An array containing all of the extensions supported for the "chat" editor
 */
export const getChatFeatureset = (extensionConfig: IChatFeatureSetConfig): Extensions => {
    return getChatFeatureBuilder(extensionConfig).build();
};

/**
 * Gets a list of all of the extensions for the chat editor including images.
 *
 * Note: `getChatWithImagesFeatureset` is a superset of `getChatFeatureset`
 * @param extensionConfig Configurations and event handler bindings for the various extensions
 * @returns An array containing all of the extensions supported for the "chat" editor, with image support
 */
export const getChatWithImagesFeatureset = (
    extensionConfig: IChatWithImageFeatureSetConfig,
): Extensions => {
    return getChatFeatureBuilder(extensionConfig).withImage(extensionConfig).build();
};
